import React from 'react';
import { cx } from './cx';
import { IconClose } from '../icons';

// §5 — the only place shadow is permitted. One token, `shadow-overlay`, plus
// the backdrop. §4 — radius 0, no border: the shadow and the value step do the
// separating. §3 — panel density throughout.
//
// Behaviour is deliberately identical to the hand-rolled modals this replaces:
// click the backdrop to close, clicks inside stop propagating. No focus trap
// and no escape handler are added here — neither existed before, and inventing
// them in a token migration would be a behaviour change hiding in a restyle.
// Worth doing, but as its own change.
const SIZES = {
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
};

export default function Modal({ onClose, size = 'lg', className, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-backdrop backdrop-blur-[2px] p-space-6"
      onClick={onClose}
    >
      <div
        className={cx(
          'flex w-full flex-col bg-carbon-850 shadow-overlay rounded-radius-none max-h-[85vh]',
          SIZES[size],
          className,
        )}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// Header, body and footer are separate exports rather than props so a modal
// can put whatever it needs between them — the export button in ViewBoardModal,
// the filter row in CompareModal — without the primitive growing a slot for
// each one.
export function ModalHeader({ title, onClose, actions, className, children }) {
  return (
    <div
      className={cx(
        'flex items-start justify-between gap-space-5 p-space-6 border-b border-carbon-800',
        className,
      )}
    >
      <div className="min-w-0">
        {title && <h2 className="text-title text-carbon-050">{title}</h2>}
        {children}
      </div>
      <div className="flex shrink-0 items-center gap-space-5">
        {actions}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-radius-sm text-carbon-300 transition-[color] duration-fast ease-standard hover:text-carbon-050 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <IconClose />
        </button>
      </div>
    </div>
  );
}

export function ModalBody({ className, ...rest }) {
  return <div className={cx('flex-1 overflow-y-auto p-space-6', className)} {...rest} />;
}

export function ModalSection({ className, ...rest }) {
  return <div className={cx('border-b border-carbon-800 p-space-6', className)} {...rest} />;
}
